import { overlayRoot } from './columnScale';
import { icon } from './icons';

/**
 * Avis « Nouvelle version disponible · Recharger » (PWA, A44).
 *
 * Le service worker (`src/pwa.ts`, vite-plugin-pwa en mode `prompt`) précache
 * le jeu ; quand un nouveau build est publié, il s'installe en arrière-plan et
 * **attend**. Cet avis le signale ; seul le geste du joueur (« Recharger »)
 * active la nouvelle version et recharge la page. Jamais de rechargement
 * forcé : même si la nouvelle version prend la main depuis un autre onglet,
 * la page en cours n'est pas rechargée, l'avis attend le geste.
 *
 * Pendant un combat (Expédition, Invasion), l'avis est **retenu** : il
 * couvrirait le bandeau du chrono ; il apparaît au premier écran calme suivant
 * (fin d'Expédition, Accalmie, titre…). « Plus tard » le masque jusqu'au
 * prochain changement d'écran calme.
 *
 * La logique est un réducteur **pur** (`reduceUpdate`, `isNoticeVisible`),
 * testé dans `tests/ui/updateNotice.test.ts` ; `mountUpdateNotice` n'est que
 * la couche DOM (charte F1 : `.f-notice`, bouton `.f-btn`).
 */

export const UPDATE_NOTICE_TEXT = 'Nouvelle version disponible';
export const UPDATE_RELOAD_LABEL = 'Recharger';

/** Phases pendant lesquelles l'avis est retenu (même liste que `ui/app.ts`). */
const COMBAT_PHASES: readonly string[] = ['expedition', 'invasion'];

export function isCombatPhase(phase: string): boolean {
  return COMBAT_PHASES.includes(phase);
}

export interface UpdateState {
  /** Une nouvelle version est installée (en attente ou déjà active). */
  available: boolean;
  /** La nouvelle version contrôle déjà la page (activée depuis un autre onglet). */
  activated: boolean;
  /** « Plus tard » : masqué jusqu'au prochain écran calme. */
  dismissed: boolean;
  /** Le joueur a demandé le rechargement (bouton désactivé, attente). */
  reloading: boolean;
  /** Phase de jeu courante (`game.state.phase`). */
  phase: string;
}

export type UpdateEvent =
  /** Le service worker a une nouvelle version en attente (`onNeedRefresh`). */
  | { type: 'need-refresh' }
  /** La nouvelle version a pris le contrôle de la page (`onNeedReload`). */
  | { type: 'controlling' }
  | { type: 'phase'; phase: string }
  | { type: 'dismiss' }
  | { type: 'reload-click' };

/**
 * Effet à exécuter par l'appelant : `activate` = demander au service worker en
 * attente de prendre la main (il rechargera ensuite) ; `reload` = recharger.
 */
export type UpdateEffect = 'activate' | 'reload' | null;

export function initialUpdateState(phase = 'title'): UpdateState {
  return { available: false, activated: false, dismissed: false, reloading: false, phase };
}

export function reduceUpdate(state: UpdateState, event: UpdateEvent): { state: UpdateState; effect: UpdateEffect } {
  switch (event.type) {
    case 'need-refresh':
      return { state: { ...state, available: true }, effect: null };
    case 'controlling':
      // Rechargement demandé par le joueur : on recharge. Sinon (autre onglet),
      // jamais de rechargement imposé : l'avis attend le geste.
      if (state.reloading) return { state: { ...state, available: true, activated: true }, effect: 'reload' };
      return { state: { ...state, available: true, activated: true }, effect: null };
    case 'phase': {
      const changed = event.phase !== state.phase;
      const calm = !isCombatPhase(event.phase);
      return {
        state: { ...state, phase: event.phase, dismissed: state.dismissed && !(changed && calm) },
        effect: null,
      };
    }
    case 'dismiss':
      return { state: { ...state, dismissed: true }, effect: null };
    case 'reload-click':
      if (!state.available || state.reloading) return { state, effect: null };
      return { state: { ...state, reloading: true }, effect: state.activated ? 'reload' : 'activate' };
    default:
      return { state, effect: null };
  }
}

/** L'avis est-il affiché ? (version disponible, pas « plus tard », hors combat) */
export function isNoticeVisible(state: UpdateState): boolean {
  if (!state.available) return false;
  if (state.reloading) return true;
  return !state.dismissed && !isCombatPhase(state.phase);
}

/** Balisage F1 de l'avis (sans état). */
export function updateNoticeHtml(): string {
  return (
    `${icon('revive')}<span class="update-notice-text">${UPDATE_NOTICE_TEXT}</span>` +
    `<button type="button" class="f-btn f-btn--primary f-btn--sm update-notice-reload" data-action="update-reload">${UPDATE_RELOAD_LABEL}</button>` +
    `<button type="button" class="f-ibtn update-notice-close" data-action="update-dismiss" aria-label="Plus tard">${icon('close')}</button>`
  );
}

export interface UpdateNoticeHandle {
  dispatch(event: UpdateEvent): void;
  destroy(): void;
}

/**
 * Monte l'avis (masqué) dans `#app` (`overlayRoot`) et renvoie son répartiteur
 * d'événements. `activate` est appelé au clic quand la nouvelle version attend
 * encore ; `reload` quand elle contrôle déjà la page.
 */
export function mountUpdateNotice(opts: {
  phase: string;
  activate: () => void;
  reload: () => void;
}): UpdateNoticeHandle {
  let state = initialUpdateState(opts.phase);
  const el = document.createElement('div');
  el.className = 'f-notice update-notice';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.dataset.role = 'update-notice';
  el.innerHTML = updateNoticeHtml();
  el.hidden = true;
  overlayRoot().appendChild(el);

  const reloadBtn = el.querySelector<HTMLButtonElement>('[data-action="update-reload"]');
  const closeBtn = el.querySelector<HTMLButtonElement>('[data-action="update-dismiss"]');

  function render(): void {
    el.hidden = !isNoticeVisible(state);
    if (reloadBtn) {
      reloadBtn.disabled = state.reloading;
      reloadBtn.textContent = state.reloading ? 'Rechargement…' : UPDATE_RELOAD_LABEL;
    }
    if (closeBtn) closeBtn.hidden = state.reloading;
  }

  function dispatch(event: UpdateEvent): void {
    const out = reduceUpdate(state, event);
    state = out.state;
    render();
    if (out.effect === 'activate') opts.activate();
    else if (out.effect === 'reload') opts.reload();
  }

  reloadBtn?.addEventListener('click', () => dispatch({ type: 'reload-click' }));
  closeBtn?.addEventListener('click', () => dispatch({ type: 'dismiss' }));

  return {
    dispatch,
    destroy() {
      el.remove();
    },
  };
}
